import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import Seo from '../components/Seo'
import GallerySection from '../components/GallerySection'
import ContactForm from '../components/ContactForm'
import { getCmsCollection, getCompanyInfo } from '../lib/cms'
import { getOptimizedImageUrl, getImageSrcSet } from '../lib/image'

export default function ServiceDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [service, setService] = useState(null)
  const [allServices, setAllServices] = useState([])
  const [companyInfo, setCompanyInfo] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        const [services, info] = await Promise.all([
          getCmsCollection('tjenester'),
          getCompanyInfo(),
        ])
        setAllServices(services)
        if (info) setCompanyInfo(info)

        // Find service matching current slug
        const found = services.find((s) => {
          const sSlug = typeof s.slug === 'object' ? s.slug?.da : s.slug
          const clean = sSlug ? sSlug.replace(/^tjeneste-|^service-/, '') : ''
          return (
            clean === slug ||
            sSlug === slug ||
            sSlug === `tjeneste-${slug}` ||
            sSlug === `service-${slug}` ||
            s.gallery_slug === slug ||
            s.id === slug
          )
        })

        if (found) {
          setService(found)
        } else {
          console.warn(`Service with slug ${slug} not found`)
        }
      } catch (err) {
        console.error('Failed to load service:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [slug])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-red-600" />
        <p className="mt-4 text-sm text-slate-500 font-medium">Henter ydelsesdata...</p>
      </div>
    )
  }

  if (!service) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center space-y-4">
        <h1 className="text-3xl font-bold text-slate-900">Ydelsen kunne ikke findes</h1>
        <p className="text-slate-600">Vi kunne desværre ikke finde den pågældende ydelse i vores database.</p>
        <Link
          to="/tjenester"
          className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-6 py-3 text-sm font-bold text-white shadow hover:bg-red-700 transition"
        >
          <span>Tilbage til oversigten over ydelser</span>
        </Link>
      </div>
    )
  }

  const title = service.title
  const shortDesc = service.short_description || ''
  const content = service.content || `<p>${shortDesc}</p>`
  const gallerySlug = service.gallery_slug || slug
  const image = service.featured_image?.[0]

  return (
    <>
      <Seo
        title={`${title} :: Lyn Entreprenør`}
        description={shortDesc || `${title} udført af Lyn Entreprenør ApS på Fyn.`}
        ogImage={image || 'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/fulllogo-nobuffer-5_1e74d9a2.png'}
        canonicalUrl={`https://www.lyn-ent.dk/tjenester/${slug}`}
      />

      {/* Subhero Section: Full width image at the top, text below */}
      <section className="bg-red-600 relative overflow-hidden">
        {/* Full-width hero image at top */}
        {image && (
          <div className="relative w-full h-52 sm:h-64 md:h-72 lg:h-[320px] overflow-hidden bg-slate-900">
            <img
              src={getOptimizedImageUrl(image, { width: 1920, height: 640, quality: 85 })}
              srcSet={getImageSrcSet(image, [640, 1024, 1440, 1920])}
              sizes="100vw"
              alt={title}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent pointer-events-none" />

            {/* V-shaped divider between image and red header */}
            <div className="absolute -bottom-0.5 -left-px -right-px w-[calc(100%+2px)] overflow-hidden leading-none z-10 pointer-events-none">
              <svg
                className="relative block w-full h-5 sm:h-7 md:h-9 text-red-600"
                viewBox="0 0 1200 120"
                preserveAspectRatio="none"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
              </svg>
            </div>
          </div>
        )}

        {/* Text content under the image */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-20 sm:pb-24 relative text-left">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummer" className="mb-4 flex items-center gap-2 text-xs font-medium text-red-200">
            <Link to="/" className="hover:text-white transition">Hjem</Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-red-300" aria-hidden="true" />
            <Link to="/tjenester" className="hover:text-white transition">Tjenester</Link>
            <i className="fa-solid fa-chevron-right text-[10px] text-red-300" aria-hidden="true" />
            <span className="text-white font-semibold truncate">{title}</span>
          </nav>

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
                {title}
              </h1>
            </div>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-bold text-red-600 hover:bg-red-50 transition active:scale-95 whitespace-nowrap shadow-md"
            >
              <span>Få et tilbud</span>
            </Link>
          </div>
        </div>

        {/* V-shaped divider at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-8 sm:h-12 md:h-16 lg:h-20 text-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Content + Info Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Main content body */}
            <div className="lg:col-span-8 space-y-8">
              {/* Text Description verbatim from scrape */}
              <div className="prose prose-slate max-w-none text-slate-700 text-base sm:text-lg leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </div>

              {/* Gallery Section placed directly under the text */}
              <GallerySection
                slug={gallerySlug}
                title={`Billeder fra ${title}`}
              />
            </div>

            {/* Right Sidebar: Contact & Quick Info */}
            <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
              {/* Contact Card */}
              <div className="flex flex-col justify-between rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
                {/* Card Image */}
                {image && (
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={getOptimizedImageUrl(image, { width: 600, height: 380, quality: 80 })}
                      alt={title}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                    {/* V-shaped divider at bottom of image */}
                    <div className="absolute -bottom-0.5 -left-px -right-px w-[calc(100%+2px)] overflow-hidden leading-none z-10 pointer-events-none">
                      <svg
                        className="relative block w-full h-5 sm:h-6 md:h-7 text-white"
                        viewBox="0 0 1200 120"
                        preserveAspectRatio="none"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Card Content */}
                <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      Bestil {title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                      Har du spørgsmål til opgaven, eller ønsker du en fast pris eller timepris? Vi rådgiver gerne om den bedste løsning.
                    </p>
                  </div>

                  <div className="space-y-3 text-sm">
                    <a
                      href="tel:+4555236932"
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition whitespace-nowrap"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                      <span>Ring 55 23 69 32</span>
                    </a>

                    <a
                      href="mailto:jc@lyn-ent.dk"
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl bg-white border border-slate-300 text-slate-800 font-semibold hover:bg-slate-100 transition whitespace-nowrap text-xs"
                    >
                      <i className="fa-solid fa-envelope text-red-600" aria-hidden="true" />
                      <span>Send email: jc@lyn-ent.dk</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Other Services Navigation List */}
              <div className="rounded-2xl bg-white p-6 border border-slate-200 shadow-sm">
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4">
                  Øvrige ydelser
                </h4>
                <div className="space-y-1">
                  {allServices.map((s) => {
                    const sSlug = typeof s.slug === 'object' ? s.slug?.da : s.slug
                    const clean = sSlug ? sSlug.replace(/^tjeneste-|^service-/, '') : ''
                    const isCurrent = clean === slug

                    return (
                      <Link
                        key={s.id || clean}
                        to={`/tjenester/${clean}`}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition ${
                          isCurrent
                            ? 'bg-red-50 text-red-700 font-bold'
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                        }`}
                      >
                        <span className="truncate">{s.title}</span>
                        <i className="fa-solid fa-chevron-right text-[10px] opacity-40" aria-hidden="true" />
                      </Link>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Indhent et tilbud på {title}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Udfyld formularen nedenfor, så kontakter vi dig hurtigst muligt.
            </p>
          </div>
          <ContactForm defaultService={title} />
        </div>
      </section>
    </>
  )
}
